import { useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { Progress } from "antd";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { themeAtom } from "@src/jotai/theme";
import type { UserAssetRecord } from "@apis/http/api";
import { RotatingGradientBg } from "@pages/library/UICardWrap/UIVideoGenerateCard";

interface DetailGeneratingViewProps {
  record: UserAssetRecord;
  aspectRatio?: string;
}

export const DetailGeneratingView = ({ record, aspectRatio }: DetailGeneratingViewProps) => {
  const status = record?.jobItem?.status;
  const isProcessing = status === "processing";
  const isHorizontal = aspectRatio === "RATIO_16_9" || aspectRatio === "16:9";
  const isDark = useAtomValue(themeAtom) === "dark";

  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!isProcessing) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isProcessing]);

  const progress = useMemo(() => {
    if (!record.gmtCreated || !record.gmtEstimatedCompleted) return 0;
    const start = new Date(record.gmtCreated).getTime();
    const end = new Date(record.gmtEstimatedCompleted).getTime();
    const total = end - start;
    if (total <= 0) return 95;
    const elapsed = now - start;
    return Math.min(95, Math.max(0, Math.round((elapsed / total) * 100)));
  }, [record.gmtCreated, record.gmtEstimatedCompleted, now]);

  const isOverdue = useMemo(() => {
    if (!record.gmtCreated) return false;
    const start = new Date(record.gmtCreated).getTime();
    return now - start > 10 * 60 * 1000;
  }, [record.gmtCreated, now]);

  return (
    <div className="w-full h-full flex flex-col items-center gap-[22px] pt-[47px]">
      <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
        <div
          className={classNames(
            "generate-card-item flex flex-col items-center justify-center gap-[8px] overflow-hidden",
            isHorizontal ? "w-full aspect-video max-h-full" : "h-full aspect-9/16 max-w-full",
          )}
        >
          <RotatingGradientBg showDot={status === "processing"} />
          {isProcessing ? (
            <>
              <span className="relative z-2 text-[20px] font-bold text-kds-fg">{progress}%</span>
              <Progress
                percent={progress}
                showInfo={false}
                strokeColor={isDark ? { from: "#71717A", to: "white" } : { from: "#0191F8", to: "#8466FD" }}
                className="w-[160px]! relative z-2"
              />
              {isOverdue && (
                <span className="relative z-2 text-[14px] text-kds-fg-subtle mt-[8px]">
                  <Trans>Brewing your video...</Trans>
                </span>
              )}
            </>
          ) : (
            <span className="relative z-2 text-[18px] text-kds-fg-soft">
              <Trans>Queued</Trans>
            </span>
          )}
        </div>
      </div>
      <div className="h-[22px]"></div>
    </div>
  );
};
